import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public user: Observable<firebase.User | null >;
  public isLoggedIn: boolean;
  public userToken: string | null = null;
  private tokenRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private tokenCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly tokenRefreshOffsetMs = 60_000; // Refrescar 1 minuto antes de expirar
  private readonly tokenCheckIntervalMs = 5 * 60_000; // Verificar cada 5 minutos como respaldo

  constructor(
    private _router: Router,
    public auth: AngularFireAuth,
    private cookieService: CookieService
  ) {
    this.user = this.auth.authState;
    this.isLoggedIn = false;

    // Suscripción al observable de idTokenResult para mantener el token actualizado
    this.auth.idTokenResult.subscribe((tokenResult) => {
      this.handleTokenResult(tokenResult);
    });

    this.refreshAuthenticationStatus();
  }

  login(username: string, password: string): Promise<any> {
    return this.auth.signInWithEmailAndPassword(username, password);
  }

  loginWithGoogle(): Promise<any> {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return this.auth.signInWithPopup(provider);
  }

  loginWithApple(): Promise<any> {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    // Request basic scopes Apple can return
    provider.addScope('email');
    provider.addScope('name');
    return this.auth.signInWithPopup(provider);
  }

  // Redirect variants for Safari/iOS
  loginWithGoogleRedirect(): Promise<void> {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return this.auth.signInWithRedirect(provider);
  }

  loginWithAppleRedirect(): Promise<void> {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return this.auth.signInWithRedirect(provider);
  }

  async handleRedirectResult(): Promise<firebase.auth.UserCredential | null> {
    try {
      const result = await this.auth.getRedirectResult();
      // When no redirect occurred, result.user is null
      return result?.user ? result : null;
    } catch (e) {
      // Swallow and let caller decide UX
      return null;
    }
  }

  register(userInfo: any): Promise<any> {
    return this.auth.createUserWithEmailAndPassword(userInfo.email, userInfo.password);
  }

  get authenticated(): boolean {
    return this.isLoggedIn;
  }

  get currentUser(): Observable<firebase.User | null> {
    return this.user;
  }

  refreshAuthenticationStatus(){
    this.user.subscribe((user) => {
      if (user) {
        this.isLoggedIn = true;
        // Aseguramos un token inicial y programamos su refresco
        user.getIdTokenResult().then((tokenResult) => {
          this.handleTokenResult(tokenResult);
        });
      } else {
        this.isLoggedIn = false;
        this.handleTokenResult(null);
      }
    });
  }

  verifyEmail() {
    return this.currentUser.subscribe({
      next: (user) => {
        user!.sendEmailVerification();
      },
      error: (err) => {
        console.log("verifyEmail:", err);
      }
    });
  }

  updateProfile(name?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.currentUser.subscribe({
        next: (user) => {
          if (user) {
            user.updateProfile({
              displayName: (name) ? name : user.displayName,
            }).then(resolve).catch(reject);
          } else {
            reject(new Error('No user logged in'));
          }
        },
        error: reject
      });
    });
  }

  public logout(): void {
    this.clearScheduledRefresh();
    this.clearTokenCheckInterval();
    sessionStorage.clear();
    this.cookieService.deleteAll();

    this.auth.signOut().then(() => {
      this.isLoggedIn = false;
      this._router.navigate(['/login']);
    });
  }

  resetPassword(email: string) {
    return this.auth.sendPasswordResetEmail(email);
  }

  private handleTokenResult(tokenResult: firebase.auth.IdTokenResult | null): void {
    if (!tokenResult) {
      this.userToken = null;
      this.clearScheduledRefresh();
      this.clearTokenCheckInterval();
      return;
    }

    this.userToken = tokenResult.token;
    this.scheduleTokenRefresh(tokenResult);
    this.startTokenCheckInterval();
  }

  private scheduleTokenRefresh(tokenResult: firebase.auth.IdTokenResult): void {
    this.clearScheduledRefresh();
    const expirationTime = new Date(tokenResult.expirationTime).getTime();
    const refreshIn = Math.max(5_000, expirationTime - Date.now() - this.tokenRefreshOffsetMs);
    this.tokenRefreshTimeoutId = setTimeout(() => {
      this.forceTokenRefresh();
    }, refreshIn);
  }

  private clearScheduledRefresh(): void {
    if (this.tokenRefreshTimeoutId) {
      clearTimeout(this.tokenRefreshTimeoutId);
      this.tokenRefreshTimeoutId = null;
    }
  }

  private startTokenCheckInterval(): void {
    this.clearTokenCheckInterval();
    // Verificación periódica como respaldo por si el timer principal falla
    this.tokenCheckIntervalId = setInterval(() => {
      this.checkAndRefreshTokenIfNeeded();
    }, this.tokenCheckIntervalMs);
  }

  private clearTokenCheckInterval(): void {
    if (this.tokenCheckIntervalId) {
      clearInterval(this.tokenCheckIntervalId);
      this.tokenCheckIntervalId = null;
    }
  }

  private async checkAndRefreshTokenIfNeeded(): Promise<void> {
    try {
      const currentUser = await this.auth.currentUser;
      if (!currentUser) {
        return;
      }

      const tokenResult = await currentUser.getIdTokenResult(false);
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const timeUntilExpiry = expirationTime - Date.now();

      // Si el token expira en menos de 2 minutos, refrescarlo
      if (timeUntilExpiry < 2 * 60_000) {
        await this.forceTokenRefresh();
      }
    } catch (error) {
      // Silencioso: no queremos interrumpir al usuario
    }
  }

  public async forceTokenRefresh(logoutOnFailure = false): Promise<string | null> {
    try {
      const currentUser = await this.auth.currentUser;
      if (!currentUser) {
        this.handleTokenResult(null);
        return null;
      }

      // Forzar refresco del token usando el refreshToken de Firebase
      const tokenResult = await currentUser.getIdTokenResult(true);
      this.handleTokenResult(tokenResult);
      return this.userToken;
    } catch (error) {
      // Solo cerrar sesión si realmente no hay usuario o el refreshToken es inválido
      const currentUser = await this.auth.currentUser;
      if (!currentUser || logoutOnFailure) {
        if (logoutOnFailure) {
          this.logout();
        }
        return null;
      }
      // Si hay usuario pero falló el refresco, intentar de nuevo en el siguiente ciclo
      return null;
    }
  }
}
